import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { isStaffUser, useRouter, sortByPropertyAlphabetically, useTerminology } from "utils";
import * as fromRoutes from "../actions/types";
import { Grid, Typography, Button } from "@material-ui/core";
import moment from "moment";
import "react-dates/initialize";
import { DateRangePicker, DayPickerRangeController } from "react-dates";
import "react-dates/lib/css/_datepicker.css";
import EventIcon from "@material-ui/icons/Event";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import {
  Feedback,
  FormActions,
  FormButton,
  FormSubmitButton,
  ScrollToTopOnMount,
  SelectInput,
} from "frame/components";

// Report types gated by organisation feature flags (matches navigation-filter.js)
const REPORT_FEATURE_FLAG_MAP = {
  hazards: "incidents",
  hazardous_substances: "incidents",
  leaves: "leave",
  staff_training: "staffTraining",
  accidents: "incidents",
  safety_minutes: "safetyMinutes",
  inductions: "inductionMeetings",
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(0, 3, 10, 3),
    backgroundColor: theme.palette.white,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
  },
  "@global": {
    ".DayPicker__withBorder": {
      border: "none !important",
      boxShadow: "none",
      borderRadius: 4,
    },
    ".DayPickerNavigation_leftButton__horizontalDefault": {
      left: "22px !important",
      right: "auto !important",
    },
    ".DayPickerNavigation_rightButton__horizontalDefault": {
      right: "22px !important",
      left: "auto !important",
    },
    ".DayPickerNavigation__horizontalDefault": {
      width: "auto !important",
      left: "0 !important",
      right: "0 !important",
    },
    ".DayPickerNavigation": {
      position: "relative !important",
      width: "auto !important",
    },
    ".DayPicker_weekHeader ul": {
      marginBottom: "8px !important",
    },
    ".DayPickerNavigation_button": {
      position: "absolute !important",
      width: "32px !important",
      height: "32px !important",
      padding: "0 !important",
      display: "inline-flex !important",
      alignItems: "center !important",
      justifyContent: "center !important",
      boxSizing: "border-box !important",
      top: "10px !important",
    },
    // Add space under weekday headers (Su, Mo, Tu...)
    ".DayPicker_weekHeader": {
      paddingBottom: "8px !important",
    },
    ".DayPicker_weekHeader_ul": {
      marginBottom: "8px !important",
    },
    // Fallback to nudge the grid down a bit in case header spacing is overridden
    ".CalendarMonthGrid__horizontal": {
      marginTop: "6px !important",
    },
    ".DayPickerNavigation .DayPickerNavigation_button:first-of-type": {
      left: "12px !important",
      right: "auto !important",
    },
    ".DayPickerNavigation .DayPickerNavigation_button:last-of-type": {
      right: "12px !important",
      left: "auto !important",
    },
    ".DayPicker": {
      display: "inline-block !important",
    },
    ".CalendarMonth_caption": {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
    ".CalendarDay__default": {
      background: theme.palette.white,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.divider}`,
    },
    ".CalendarDay__hovered_span, .CalendarDay__hovered_span:active, .CalendarDay__hovered_span:hover": {
      background: theme.palette.highlight,
      color: theme.palette.brand,
      border: `1px double ${theme.palette.highlight}`,
    },
    ".CalendarDay__selected_span, .CalendarDay__selected_span:active, .CalendarDay__selected_span:hover": {
      background: theme.palette.highlight,
      color: theme.palette.brand,
      border: `1px double ${theme.palette.highlight}`,
    },
    ".CalendarDay__selected, .CalendarDay__selected:active, .CalendarDay__selected:hover": {
      background: theme.palette.brand,
      color: theme.palette.white,
      border: `1px double ${theme.palette.brand}`,
    },
    ".CalendarDay__blocked_out_of_range, .CalendarDay__blocked_out_of_range:active, .CalendarDay__blocked_out_of_range:hover": {
      color: theme.palette.text.disabled,
      background: "transparent",
      border: `1px solid ${theme.palette.divider}`,
    },
    ".DayPickerNavigation_button__default": {
      background: theme.palette.brand,
      border: "none",
      color: theme.palette.white,
    },
    ".DayPickerNavigation_button__default:disabled": {
      background: theme.palette.action ? theme.palette.action.disabled : "#ccc",
      color: theme.palette.text.disabled,
    },
  },
  dateRangeLabel: {
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  dateRangeContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    display: "inline-block",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background ? theme.palette.background.paper : theme.palette.white,
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  },
  dateRangeWrapper: {
    display: "inline-block",
  },
  quickRanges: {
    marginBottom: theme.spacing(1),
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  body: {
    margin: theme.spacing(1, 0, 2, 0),
    maxWidth: 540,
    "& > *": {
      paddingLeft: "0 !important",
      // paddingRight: '0 !important',
    },
  },
  message: {
    marginBottom: theme.spacing(2),
  },
  dateSummary: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  dateRules: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

const Reports = ({
  save,
  onDownload,
  onClearResponseMessage,
  responseMessage,
  staffList,
  siteList,
  role,
  onOpenTimesheetReport,
  onOpenLeaveReport,
  onOpenEquipmentUsageReport,
  featureFlags,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down("xs"));

  const defaultFrom = moment().subtract(7, "days").startOf("day");
  const defaultTo = moment().startOf("day");

  const [values, setValues] = useState({
    from: defaultFrom.format(),
    to: defaultTo.format(),
  });

  const [range, setRange] = useState({
    startDate: defaultFrom,
    endDate: defaultTo,
  });
  const [focusedInput, setFocusedInput] = useState("startDate");
  const [limitMessage, setLimitMessage] = useState("");
  // const [showErrorMessage, setShowErrorMessage] = useState(false)

  // to display response message for 3 seconds only
  // useEffect(() => {
  //   if(responseMessage) {
  //     setShowErrorMessage(true)
  //     setTimeout(() => {
  //       setShowErrorMessage(false)
  //     }, 3000)
  //   }
  // }, [responseMessage])

  useEffect(() => {
    onClearResponseMessage();
  }, [onClearResponseMessage]);

  const { saving } = save;
  const { history } = useRouter();
  const { siteLabel } = useTerminology();

  if (isStaffUser(role)) {
    history.push(fromRoutes.ACCOUNT_PROFILE_PATH);
  }

  const onSubmit = (event) => {
    event.preventDefault();
    if (!saving) {
      onDownload(values);
    }
  };

  const onReset = () => {
    setValues({
      from: defaultFrom.format(),
      to: defaultTo.format(),
    });
    setRange({
      startDate: defaultFrom,
      endDate: defaultTo,
    });
    setFocusedInput("startDate");
  };

  const onSelectOption = (key) => (value) => {
    if (key === "report") {
      //clear any error message on report dropdown toggle
      onClearResponseMessage();
    }

    setValues({
      ...values,
      [key]: value,
    });
  };

  const onSelectRange = ({ startDate, endDate }) => {
    setRange({ startDate, endDate });
    // clear any local limit message when changing dates
    if (limitMessage) {
      setLimitMessage("");
    }
    setValues({
      ...values,
      from: startDate ? moment(startDate).startOf("day").format() : values.from,
      to: endDate ? moment(endDate).startOf("day").format() : values.to,
    });
  };

  const applyQuickRange = (key) => {
    let startDate;
    let endDate;

    switch (key) {
      // ISO weeks are Monday (start) to Sunday (end)
      case "this_week": {
        startDate = moment().startOf("isoWeek");
        endDate = moment().endOf("isoWeek");
        break;
      }
      case "today": {
        const todayStart = moment().startOf("day");
        const todayEnd = todayStart.clone();
        startDate = todayStart;
        endDate = todayEnd;
        break;
      }
      case "last_week": {
        const lastWeekStart = moment().subtract(1, "week").startOf("isoWeek");
        const lastWeekEnd = moment().subtract(1, "week").endOf("isoWeek");
        startDate = lastWeekStart;
        endDate = lastWeekEnd;
        break;
      }
      case "past_two_weeks": {
        // Two full weeks immediately before the current week
        startDate = moment().subtract(2, "weeks").startOf("isoWeek");
        endDate = moment().subtract(1, "weeks").endOf("isoWeek");
        break;
      }
      case "this_month": {
        startDate = moment().startOf("month");
        endDate = moment().endOf("month");
        break;
      }
      case "last_month": {
        const lastMonthStart = moment().subtract(1, "month").startOf("month");
        const lastMonthEnd = moment().subtract(1, "month").endOf("month");
        startDate = lastMonthStart;
        endDate = lastMonthEnd;
        break;
      }
      default:
        return;
    }

    setRange({ startDate, endDate });
    setValues({
      ...values,
      from: startDate.startOf("day").format(),
      to: endDate.startOf("day").format(),
    });
    setFocusedInput("startDate");
    if (limitMessage) {
      setLimitMessage("");
    }
  };

  const allOptions = [
    {
      label: "Hazards",
      value: "hazards",
    },
    {
      label: "Hazardous Substances",
      value: "hazardous_substances",
    },
    {
      label: "Leave",
      value: "leaves",
    },
    {
      label: "Staff Activity",
      value: "staff",
    },
    {
      label: "Staff Training",
      value: "staff_training",
    },
    {
      label: "Incidents",
      value: "accidents",
    },
    {
      label: "Timesheets",
      value: "timesheets",
    },
    {
      label: "Equipment Usage",
      value: "equipment_usage",
    },
    {
      label: "Safety Minutes",
      value: "safety_minutes",
    },
    {
      label: "Induction Meetings",
      value: "inductions",
    },
  ];

  const isReportEnabled = (reportValue) => {
    if (reportValue === "equipment_usage") {
      return (
        featureFlags?.equipment === true &&
        featureFlags?.equipmentUsageRecording === true
      );
    }
    const flagName = REPORT_FEATURE_FLAG_MAP[reportValue];
    if (!flagName) {
      return true;
    }
    // Equipment is opt-in
    if (flagName === "equipment") {
      return featureFlags?.equipment === true;
    }
    // Staff Training is opt-in / temporarily hard-hidden (STAFF_TRAINING_UI_ENABLED)
    if (flagName === "staffTraining") {
      return featureFlags?.staffTraining === true;
    }
    // Default enabled when flag is unset
    return featureFlags?.[flagName] !== false;
  };

  // Hide report types for modules disabled via feature flags
  const options = allOptions
    .filter((option) => isReportEnabled(option.value))
    .sort(sortByPropertyAlphabetically("label"));

  useEffect(() => {
    if (
      values.report === "equipment_usage" &&
      featureFlags &&
      !featureFlags.fetch?.loading &&
      !isReportEnabled("equipment_usage")
    ) {
      setValues((prev) => ({ ...prev, report: "" }));
    }
  }, [featureFlags, values.report]);

  let staffOptions = (staffList || [])
    .map((s) => ({ label: s.fullName, value: s.id }))
    .sort(sortByPropertyAlphabetically("label"));

  let siteOptions = (siteList || [])
    .map((s) => ({ label: s.siteName, value: s.id }))
    .concat([{ label: "All", value: "all" }])
    .sort(sortByPropertyAlphabetically("label"));

  const IsHazardOrSubstance =
    values.report === "hazards" || values.report === "hazardous_substances";
  const ShouldShowSiteSelect =
    IsHazardOrSubstance || values.report === "timesheets" || values.report === "equipment_usage";

  const getMonthLimit = () => {
    // For Timesheets: All sites => 3 months, Specific site => 6 months
    if (values.report === "timesheets") {
      return !values.siteId || values.siteId === "all" ? 3 : 6;
    }
    return null;
  };

  const isRangeWithinLimit = () => {
    const limit = getMonthLimit();
    if (!limit) return true;
    const from = moment(values.from).startOf("day");
    const to = moment(values.to).startOf("day");
    // Block if end is strictly after from + limit months (inclusive limit)
    return !to.isAfter(from.clone().add(limit, "months"));
  };

  const getLimitExceededMessage = () => {
    const isAll = !values.siteId || values.siteId === "all";
    const limit = getMonthLimit();
    const scope = isAll ? "All sites" : "selected site";
    return `The chosen date range exceeds the ${limit}-month limit for ${scope}. For larger exports, contact info@fortherecord.pro.`;
  };

  const handleTimesheetOpen = () => {
    if (values.report === "timesheets") {
      if (!isRangeWithinLimit()) {
        setLimitMessage(getLimitExceededMessage());
        return;
      }
      setLimitMessage("");
      onOpenTimesheetReport(values);
      return;
    }
    if (values.report === "equipment_usage") {
      onOpenEquipmentUsageReport(values);
    }
  };

  const handleDownload = () => {
    if (values.report === "timesheets") {
      if (!isRangeWithinLimit()) {
        setLimitMessage(getLimitExceededMessage());
        return;
      }
      setLimitMessage("");
    }
    onDownload(values);
  };

  return (
    <div className={classes.root}>
      <ScrollToTopOnMount />
      <form autoComplete="off" noValidate onSubmit={onSubmit}>
        

        <Grid className={classes.body} container spacing={2}>
          <Grid item xs={12}>
            <SelectInput
              id={"report_type"}
              disabled={saving}
              label="Report Type"
              options={options}
              value={values.report}
              onChange={onSelectOption("report")}
            />
          </Grid>

          {values.report === "staff" && (
            <Grid item xs={12}>
              <SelectInput
                id={"staff_id"}
                disabled={saving}
                label="Staff"
                options={staffOptions}
                value={values.staffId}
                onChange={onSelectOption("staffId")}
              />
            </Grid>
          )}

          {ShouldShowSiteSelect && (
            <Grid item xs={12}>
              <SelectInput
                noNone
                id={"site_id"}
                disabled={saving}
                label={siteLabel}
                options={siteOptions}
                value={values.siteId || "all"}
                onChange={onSelectOption("siteId")}
              />
            </Grid>
          )}
          {values.report && !IsHazardOrSubstance && (
            <>
              <Grid item xs={12}>
                <Typography variant="caption" className={classes.dateRangeLabel}>
                  Date Range
                </Typography>
                <div className={classes.dateRangeWrapper} style={{ pointerEvents: saving ? "none" : "auto", opacity: saving ? 0.6 : 1 }}>
                  <div className={classes.quickRanges}>
                    <Button size="small" variant="outlined" onClick={() => applyQuickRange("today")}>
                      Today
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => applyQuickRange("this_week")}>
                      This week
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => applyQuickRange("last_week")}>
                      Last week
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => applyQuickRange("past_two_weeks")}>
                      Past two weeks
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => applyQuickRange("this_month")}>
                      This month
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => applyQuickRange("last_month")}>
                      Last Month
                    </Button>
                  </div>
                  {/* Data limits note */}
                  {values.report === "timesheets" && (
                    <Typography variant="caption" className={classes.dateRules}>
                      Data limits: All sites up to 3 months; specific site up to 6 months.
                    </Typography>
                  )}
                  <div className={classes.dateRangeContainer}>
                    <DayPickerRangeController
                      startDate={range.startDate}
                      endDate={range.endDate}
                      firstDayOfWeek={1}
                      onDatesChange={({ startDate, endDate }) => {
                        // If a full range exists and user clicks a new date, start a new range
                        if (focusedInput === "startDate" && startDate) {
                          endDate = null;
                        }
                        onSelectRange({ startDate, endDate });
                        setFocusedInput(startDate && !endDate ? "endDate" : "startDate");
                      }}
                      focusedInput={focusedInput || "startDate"}
                      numberOfMonths={mobileView ? 1 : 2}
                      initialVisibleMonth={() => (mobileView ? moment() : moment().subtract(1, "month"))}
                      daySize={mobileView ? 36 : 40}
                      isOutsideRange={(day) =>
                        values.report !== "leaves" ? day.isAfter(moment(), "day") : false
                      }
                      minimumNights={0}
                      navPrev={<ChevronLeftIcon />}
                      navNext={<ChevronRightIcon />}
                      hideKeyboardShortcutsPanel
                    />
                  </div>
                   <Typography variant="body1" className={classes.dateSummary}>
                     {range.startDate ? moment(range.startDate).format("DD MMM YYYY") : "ΓÇö"}{" "}
                     to{" "}
                     {range.endDate ? moment(range.endDate).format("DD MMM YYYY") : "ΓÇö"}
                     {range.startDate && range.endDate
                       ? ` (${moment(range.endDate).startOf("day").diff(moment(range.startDate).startOf("day"), "days") + 1} days)`
                       : ""}
                   </Typography>
                  {limitMessage && (
                    <Feedback active color="error" disableTimeout>
                      {limitMessage}
                    </Feedback>
                  )}
                </div>
              </Grid>
            </>
          )}
        </Grid>
        <FormActions>
          {values.report === "timesheets" && (
            <FormButton
              disabled={saving}
              loading={saving}
               onClick={handleTimesheetOpen}
              color="brand"
            >
              Open Timesheet Report
            </FormButton>
          )}
          {values.report === "equipment_usage" &&
            featureFlags?.equipment === true &&
            featureFlags?.equipmentUsageRecording === true && (
            <FormButton
              disabled={saving}
              loading={saving}
              onClick={handleTimesheetOpen}
              color="brand"
            >
              Open Equipment Usage Report
            </FormButton>
          )}
          {values.report === "leaves" && featureFlags?.leave !== false && (
            <FormButton
              disabled={saving}
              onClick={() => onOpenLeaveReport(values)}
              color="brand"
            >
              Open Leave Report
            </FormButton>
          )}
          <FormButton disabled={saving} onClick={onReset}>
            Reset
          </FormButton>
          <FormButton
            disabled={
              !values.report || (values.report === "staff" && !values.staffId)
            }
            loading={saving}
            onClick={handleDownload}
          >
            Download CSV
          </FormButton>
        </FormActions>
        {responseMessage !== "" && (
          <Feedback
            disableTimeout
            active
            color={
              responseMessage.toLowerCase().includes("success")
                ? "success"
                : "error"
            }
          >
            {responseMessage === "Invalid model" ||
            responseMessage.includes("Object reference")
              ? "Error occurred. Please try again or contact support."
              : responseMessage}
          </Feedback>
        )}
      </form>
    </div>
  );
};

Reports.propTypes = {
  reports: PropTypes.object,
  siteList: PropTypes.array.isRequired,
  staffList: PropTypes.array.isRequired,
  save: PropTypes.exact({
    done: PropTypes.bool,
    error: PropTypes.string,
    saving: PropTypes.bool,
    success: PropTypes.bool,
  }),
  onDownload: PropTypes.func.isRequired,
  onClearResponseMessage: PropTypes.func.isRequired,
  responseMessage: PropTypes.string,
  role: PropTypes.string.isRequired,
  onOpenTimesheetReport: PropTypes.func.isRequired,
  onOpenLeaveReport: PropTypes.func.isRequired,
  onOpenEquipmentUsageReport: PropTypes.func.isRequired,
  featureFlags: PropTypes.object,
};

export default Reports;
